import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import { useBlockLayout, useGlobalFilter, useResizeColumns, useRowSelect, useSortBy, useTable } from 'react-table';
import classNames from 'classnames';

import DownArrow from '../../assets/svgComponents/DownArrow';
import DownArrowDisabled from '../../assets/svgComponents/DownArrowDisabled';
import UpArrow from '../../assets/svgComponents/UpArrow';
import { useWindowSize } from '../../hooks/useWindowResize';
import Pagination from '../Pagination/Pagination';
import EmptyTable from '../shared/EmptyTable/EmptyTable';
import TableLoader from '../shared/SkeletonLoaders/TableLoader';
import CustomTooltip from '../shared/Tooltip/Tooltip';

import { Checkbox } from './CustomCheckbox';

import styles from './ReactTable.module.scss';

const CustomCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return <Checkbox color="primary" type="checkbox" ref={resolvedRef} {...rest} />;
});

const Header = ({ getToggleAllRowsSelectedProps }) => <CustomCheckbox {...getToggleAllRowsSelectedProps()} />;
const Cell = ({ row }) => <CustomCheckbox {...row.getToggleRowSelectedProps()} />;

const ReactTable = ({
  columns,
  data,
  onSelectRow,
  selectedItems,
  defaultSortingState = {},
  setSortingState = () => {},
  customStyle = '',
  showCheckBox = false,
  defaultStyling = {
    table_margin: '',
    header_height: '',
    memberTableHeader: '',
  },
  disableColumnResizing = [],
  defaultFeildSize = 0,
  setPage,
  setPerPage,
  pageInfo,
  emptyIcon,
  emptyTitle,
  emptyCTA,
  emptyCTATitle,
  emptyCTAIcon,
  loading,
  perPage,
  showTooltip = false,
}) => {
  const size = useWindowSize();

  const defaultColumn = useMemo(
    () => ({
      width: ((size.width - 290) / (columns.length + (showCheckBox ? 1 : 0))) * defaultFeildSize,
      maxWidth: 1100,
      minWidth: 200,
    }),
    [size]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, selectedFlatRows, state } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        sortBy: defaultSortingState,
        hiddenColumns: ['id'],
      },
      autoResetResize: false,
    },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    useRowSelect,
    useBlockLayout,
    (hooks) => {
      if (showCheckBox) {
        hooks.visibleColumns.push((col) => [
          { id: 'selection', Header, Cell, minWidth: 50, width: 50, maxWidth: 50 },
          ...col,
        ]);
      }
    }
  );

  useEffect(() => {
    if (selectedFlatRows.length !== selectedItems.length) {
      onSelectRow(selectedFlatRows.map((row) => row.original.id * 1));
    }
  }, [selectedFlatRows, selectedItems]);

  useEffect(() => {
    setSortingState(state.sortBy);
  }, [state.sortBy]);

  const isRowSelected = useCallback(
    (id) => {
      if (selectedFlatRows.find((row) => row.original.id === id)) {
        return 'selected';
      }

      return 'not-selected';
    },
    [selectedFlatRows]
  );

  const setArrow = useCallback((col) => (col.isSortedDesc ? <DownArrow /> : <UpArrow />), []);

  const displayToolTip = useCallback(
    (row, index) => index === 1 && showTooltip && row.values.opening.length > 40,
    [showTooltip]
  );

  const getText = useCallback((row) => row.values.opening, []);

  return (
    <>
      <div className={`${styles.container} ${defaultStyling.table_margin}`}>
        <div className={styles.overflowXClip}>
          <div className={styles.overflowYHidden}>
            <Table {...getTableProps()} responsive>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()} className={`tr ${defaultStyling.memberTableHeader}`}>
                    {headerGroup.headers.map((column) => (
                      <th
                        className={`th ${column?.style}`}
                        {...column.getHeaderProps({
                          style: { minWidth: column.minWidth, width: column.width },
                        })}
                      >
                        <div className={defaultStyling.header_height}>
                          <div
                            onClick={() => column.toggleSortBy?.(null, true)}
                            className={styles.headerContent}
                            aria-hidden="true"
                          >
                            {column.render('Header')}
                            {column.id !== 'selection' && column.id !== 'multi-selected' && (
                              <span className="ps-4">
                                {column.isSorted
                                  ? setArrow(column)
                                  : column.id !== 'selection' && column.id !== 'action' && <DownArrowDisabled />}
                              </span>
                            )}
                          </div>
                          {!disableColumnResizing.includes(column.id) && (
                            <div className={styles.resizer} {...column.getResizerProps()}>
                              <div className={classNames({ isResizing: column.isResizing })} />
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {loading ? (
                  <TableLoader rows={perPage} />
                ) : (
                  rows.map((row, rowIndex) => {
                    prepareRow(row);
                    return (
                      <tr
                        className={`${styles.tRow} ${isRowSelected(row.original.id)} ${customStyle}`}
                        {...row.getRowProps()}
                      >
                        {row.cells.map((cell, index) => (
                          <td
                            {...cell.getCellProps([
                              {
                                className: classNames([
                                  styles.tData,
                                  cell.column.className,
                                  cell.column?.style,
                                  { resizer: cell.column.id !== 'selection', isResizingRow: cell.column.isResizing },
                                ]),
                                width: cell.column.width,
                              },
                            ])}
                          >
                            {displayToolTip(rows[rowIndex], index) ? (
                              <CustomTooltip text={getText(rows[rowIndex])} delay={500}>
                                <div className={`${styles.dataContent} text-truncate`}>{cell.render('Cell')}</div>
                              </CustomTooltip>
                            ) : (
                              <div className={styles.dataContent}>{cell.render('Cell')}</div>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
            {!loading && data.length === 0 && (
              <EmptyTable
                icon={emptyIcon}
                title={emptyTitle}
                callToAction={emptyCTA}
                callToActionIcon={emptyCTAIcon}
                callToActionTitle={emptyCTATitle}
              />
            )}
          </div>
        </div>
      </div>
      {data.length !== 0 && <Pagination setPage={setPage} setPerPage={setPerPage} pageInfo={pageInfo} />}
    </>
  );
};
export default ReactTable;
